import { Component, computed, inject, input, OnInit, signal, viewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { forkJoin } from "rxjs";

import { AdaaHelper } from "../../../core/utils";
import { noWhitespaceValidator } from "../../../core/validators";
import {
  FormActionButtonsComponent,
  FormCheckboxComponent,
  FormDropdownComponent,
  FormDropdownMultiComponent,
  FormInputComponent,
  MetricsReadingDetailsComponent,
  MetricUnlinkInformationComponent,
} from "../../../shared/components";
import { genericFloatButtons } from "../../../shared/components/general/float-action";
import { Constants } from "../../../shared/constants/constants";
import { AdaaBoolean, PageMode } from "../../../shared/constants/enums";
import {
  DataEntryMetricModel,
  MetricDimensionValue,
  MonitoredKpiModelType,
  ParentMetric,
  PropTypeModelType,
  ValidatorModelType,
  ValueText,
} from "../../../shared/models";
import {
  EntitiesApiService,
  MetricApiService,
  PropertiesService,
  SystemLayoutService,
  ValidatorApiService,
} from "../../../shared/services";
import { MetricsDimensionsEditorComponent } from "./metrics-dimensions-editor/metrics-dimensions-editor";

@Component({
  selector: "adaa-metrics-editor",
  imports: [
    ReactiveFormsModule,
    FormInputComponent,
    FormDropdownComponent,
    FormDropdownMultiComponent,
    FormCheckboxComponent,
    FormActionButtonsComponent,
    TranslatePipe,
    MetricsDimensionsEditorComponent,
    MetricUnlinkInformationComponent,
    MetricsReadingDetailsComponent,
  ],
  templateUrl: "./metrics-editor.html",
  styleUrl: "./metrics-editor.scss",
})
export class MetricsEditorComponent implements OnInit {
  private readonly _propsService = inject(PropertiesService);
  private readonly _metricsApiService = inject(MetricApiService);
  private readonly _entitiesApiService = inject(EntitiesApiService);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _validatorApiService = inject(ValidatorApiService);
  private readonly _translateService = inject(TranslateService);
  private readonly _toastrService = inject(ToastrService);
  private readonly _router = inject(Router);
  private readonly _systemLayoutService = inject(SystemLayoutService);

  pageMode = input.required<string>();
  id = input.required<string>();

  private _untilDestroy = AdaaHelper.untilDestroyed();
  AdaaHelper = AdaaHelper;

  unlinkModal = viewChild.required<MetricUnlinkInformationComponent>("unlink");
  private readonly _genericFloatButtons = genericFloatButtons();

  submitted: boolean = false;
  metricsForm: FormGroup;
  formValidation = signal<ValidatorModelType | undefined>(undefined);
  metric = signal<DataEntryMetricModel | undefined>(undefined);
  entityId = signal<number | undefined>(undefined);
  PageMode = PageMode;
  dataTypes = signal<ValueText[]>([]);
  dashboardTypes = signal<ValueText[]>([]);
  mainTypes = signal<ValueText[]>([]);
  entities = signal<ValueText[]>([]);
  relatedKpis = signal<MonitoredKpiModelType[]>([]);
  monthDifferenceOptions = signal<ValueText[]>([]);

  public get displayLabel(): string {
    const nameEN = this.metricsForm.get("nameEN")?.value;
    const nameAE = this.metricsForm.get("nameAE")?.value;

    return AdaaHelper.getPageLabel(nameAE, nameEN);
  }

  public get isDimensionMetric(): boolean {
    return this.metricsForm.get("type")?.value === Constants.METRICS_TYPE.DIMENSION;
  }

  public get isGlobalMetric(): boolean {
    return this.metricsForm.get("type")?.value === Constants.METRICS_TYPE.GLOBAL;
  }

  isViewMode = computed(() => this.pageMode() === PageMode.view);

  public ngOnInit(): void {
    this._systemLayoutService.hasActiveEntityChanged$.pipe(this._untilDestroy()).subscribe({
      next: () => this.goBack(),
    });

    this._prepareForm();
    this._initPage();

    if (this.id()) this._getMetric();
  }

  public submit() {
    this._updateValidationByMetricType();
    this.submitted = true;

    if (!this._checkMonitoringIsValid()) {
      this._toastrService.error(this._translateService.instant("metrics.showError.dimension_error"));
      return;
    }

    if (this.metricsForm.invalid) {
      this._toastrService.error(this._translateService.instant("notification.warning.missing_info"));
      return;
    }

    const result = {
      ...this.metric(),
      ...this.metricsForm.getRawValue(),
      status: Constants.OBJECT_STATUS.ACTIVE,
    };

    //If the metrics type is Dimenstion delete the entityIds.It's causing duplication in the metric itself.
    if (this.metricsForm.get("type")?.value === Constants.METRICS_TYPE.DIMENSION) {
      delete result.entityIds;
    }

    this._metricsApiService.saveMetric(this.pageMode() === PageMode.edit, result).subscribe({
      next: (response) => {
        if (response.inError) return;
        this._toastrService.success(this._translateService.instant("notification.success.save"));
        this.goBack();
      },
    });
  }

  public goBack() {
    this._router.navigateByUrl("/console/metrics");
  }

  public metricTypeChange(value: string) {
    if (this.isViewMode()) return;

    if (value !== Constants.METRICS_TYPE.GLOBAL) this.metricsForm.get("aiUsage")?.setValue(false);

    if (value === Constants.METRICS_TYPE.DIMENSION) {
      this.metricsForm.get("entityIds")?.setValue(null);
      this.metricsForm.get("dataSourceAE")?.disable();
      this.metricsForm.get("dataSourceEN")?.disable();
    } else {
      if (value === Constants.METRICS_TYPE.GLOBAL) {
        this.metricsForm.get("entityIds")?.setValue(null);
        this.metricsForm.get("entityIds")?.disable();
      } else {
        this.metricsForm.get("entityIds")?.enable();
      }

      this.metricsForm.get("parentMetricEntityId")?.setValue(null);
      this.metricsForm.get("dataSourceAE")?.enable();
      this.metricsForm.get("dataSourceEN")?.enable();
    }
  }

  public parentMetricChanged(value: ParentMetric) {
    this.metricsForm.get("nameAE")?.setValue(value.nameAE);
    this.metricsForm.get("nameEN")?.setValue(value.nameEN);
    this.metricsForm.get("dscAE")?.setValue(value.nameAE);
    this.metricsForm.get("dscEN")?.setValue(value.nameEN);
    this.metricsForm.get("dataSourceAE")?.setValue(value.dataSourceAE);
    this.metricsForm.get("dataSourceEN")?.setValue(value.dataSourceEN);
  }

  private _initPage() {
    forkJoin({
      props: this._propsService.getPropByIdList([
        Constants.CONSTANT_MEASUREMENT_UNIT_METRICS,
        Constants.CONSTANT_METRICS_MONTH_DIFFERENCE,
      ]),
      dashboardTypes: this._metricsApiService.getAvailableDashboardTypes(),
      entities: this._entitiesApiService.getAll(AdaaBoolean.Y, AdaaBoolean.N),
      translations: this._translateService.get([
        "main_types.G",
        "main_types.L",
        "main_types.D",
        "metrics.metrics_used_kpi",
      ]),
      ...(this.id() ? { relatedKPIs: this._metricsApiService.getRelatedKpis(+this.id()) } : {}),
    }).subscribe({
      next: (results) => {
        this._handlePropsResponse(results.props.responseData ?? []);

        this.dashboardTypes.set(
          AdaaHelper.setDropdownArray(
            results.dashboardTypes.responseData ?? [],
            "id",
            AdaaHelper.getFieldLanguage("name")
          )
        );

        const entities = results.entities.responseData ?? [];
        this.entities.set(
          AdaaHelper.setDropdownArray(
            AdaaHelper.isPMOEntity()
              ? entities.filter((e) => e.id !== Constants.CONSTANT_PMO_ID)
              : entities.filter((e) => e.id === AdaaHelper.entity.id),
            "id",
            AdaaHelper.getFieldLanguage("name")
          )
        );

        this.mainTypes.set([
          { value: Constants.METRICS_TYPE.GLOBAL, text: results.translations["main_types.G"] },
          { value: Constants.METRICS_TYPE.LOCAL, text: results.translations["main_types.L"] },
          { value: Constants.METRICS_TYPE.DIMENSION, text: results.translations["main_types.D"] },
        ]);

        if (results.relatedKPIs?.responseData) this.relatedKpis.set(results.relatedKPIs.responseData);
      },
    });
  }

  private _handlePropsResponse(props: PropTypeModelType[]) {
    const valueTypes = props.filter((e) => e.propType === Constants.CONSTANT_MEASUREMENT_UNIT_METRICS);
    const monthDifference = props.filter((e) => e.propType === Constants.CONSTANT_METRICS_MONTH_DIFFERENCE);

    this.dataTypes.set(AdaaHelper.setDropdownArray(valueTypes, "id", AdaaHelper.getFieldLanguage("name")));
    const monthDifferenceOptions = AdaaHelper.setDropdownArray(
      monthDifference,
      "value",
      AdaaHelper.getFieldLanguage("name")
    );
    monthDifferenceOptions.forEach((e) => (e.value = +e.value));
    this.monthDifferenceOptions.set(monthDifferenceOptions);
  }

  private _prepareForm() {
    const isEditAndNotPmo = this.pageMode() === PageMode.edit && !AdaaHelper.isPMOEntity();

    this.metricsForm = this._formBuilder.group({
      nameAE: { value: null, disabled: this.isViewMode() },
      nameEN: { value: null, disabled: this.isViewMode() },
      dscAE: { value: null, disabled: this.isViewMode() },
      dscEN: { value: null, disabled: this.isViewMode() },
      dataSourceAE: { value: null, disabled: this.isViewMode() },
      dataSourceEN: { value: null, disabled: this.isViewMode() },
      dataType: { value: null, disabled: this.isViewMode() },
      type: { value: null, disabled: this.isViewMode() || isEditAndNotPmo },
      dashboardType: { value: null, disabled: this.isViewMode() },
      entityIds: { value: null, disabled: this.isViewMode() || isEditAndNotPmo },
      available: { value: AdaaBoolean.N, disabled: this.isViewMode() },
      validForFuture: { value: AdaaBoolean.N, disabled: this.isViewMode() },
      aiUsage: { value: false, disabled: this.isViewMode() },
      monthDifference: { value: 0, disabled: this.isViewMode() },
      parentMetricEntityId: { value: null, disabled: this.isViewMode() },
      parentMetricId: { value: null, disabled: this.isViewMode() },
      dimensions: { value: null, disabled: this.isViewMode() },
    });

    this._validatorApiService
      .applyValidationToForm(Constants.VALIDATORS_CONF_KEY.VALIDATION_METRICS, this.metricsForm)
      .subscribe({
        next: (response) => {
          this.formValidation.set(response.validation);
          this.metricsForm = response.form;
        },
      });

    if (!AdaaHelper.isPMOEntity() && this.pageMode() === PageMode.create) {
      this.metricsForm.get("type")?.setValue(Constants.METRICS_TYPE.LOCAL);
      this.metricsForm.get("entityIds")?.setValue([AdaaHelper.entity.id]);
      this.metricsForm.get("type")?.disable({ onlySelf: true, emitEvent: false });
      this.metricsForm.get("entityIds")?.disable({ onlySelf: true, emitEvent: false });
    }
  }

  private _handleControls(type: string | undefined, synced: AdaaBoolean | undefined) {
    if (type !== Constants.METRICS_TYPE.LOCAL && synced === AdaaBoolean.N) {
      this.metricsForm.get("dataType")?.disable({ onlySelf: true, emitEvent: false });
      this.metricsForm.get("type")?.disable({ onlySelf: true, emitEvent: false });
      this.metricsForm.get("entityIds")?.disable({ onlySelf: true, emitEvent: false });
    } else if (type === Constants.METRICS_TYPE.GLOBAL && synced === AdaaBoolean.Y) {
      Object.keys(this.metricsForm.controls).forEach((controlName) => {
        if (controlName === "available") return;
        const control = this.metricsForm.get(controlName);
        control?.disable({ onlySelf: true, emitEvent: false });
      });
    }
  }

  private _getMetric() {
    this._metricsApiService.getById(+this.id()).subscribe({
      next: (response) => {
        if (response.inError) {
          this._toastrService.error(this._translateService.instant("notification.error.unknown_object"));
          this.goBack();
          return;
        }

        if (this._notAllowedToEditMonitoredMetric(response.responseData)) {
          this.goBack();
          return;
        }

        this.metricsForm.patchValue(response.responseData);
        this.metricsForm.get("monthDifference")?.setValue(response.responseData.monthDifference ?? "0");

        this.metric.set(response.responseData);

        this._floatActions(this.metric()!);
        this._handleControls(response.responseData.type, response.responseData.synced);
      },
    });
  }

  private _updateValidationByMetricType() {
    const monitoringFields = ["dashboardType", "parentMetricEntityId", "parentMetricId"];
    if (this.metricsForm.get("type")?.value === Constants.METRICS_TYPE.DIMENSION) {
      monitoringFields.forEach((e) => {
        this.metricsForm.get(e)?.setValidators([Validators.required, noWhitespaceValidator()]);
        this.metricsForm.get(e)?.updateValueAndValidity();
      });
      this.metricsForm.get("entityIds")?.clearValidators();
      this.metricsForm.get("entityIds")?.updateValueAndValidity();
    } else {
      monitoringFields.forEach((e) => {
        this.metricsForm.get(e)?.clearValidators();
        this.metricsForm.get(e)?.updateValueAndValidity();
      });
      this.metricsForm.get("entityIds")?.setValidators([Validators.required, noWhitespaceValidator()]);
      this.metricsForm.get("entityIds")?.updateValueAndValidity();
    }

    this.metricsForm.updateValueAndValidity();
  }

  private _checkMonitoringIsValid(): boolean {
    if (this.metricsForm.get("type")?.value !== Constants.METRICS_TYPE.DIMENSION) return true;

    const dimensions = (this.metricsForm.get("dimensions")?.value as MetricDimensionValue[]) ?? [];

    return dimensions.filter((e) => !e.dimensionValueIds?.length).length <= 0;
  }

  private _notAllowedToEditMonitoredMetric(metric: DataEntryMetricModel): boolean {
    const userEntityId = AdaaHelper.getLocalStorage("user", { type: "prop", property: "entityId" });
    const isPmoUser = AdaaHelper.isPMOEntity(Number(userEntityId));

    return this.pageMode() === PageMode.edit && metric.type === Constants.METRICS_TYPE.DIMENSION && !isPmoUser;
  }

  private _floatActions(metric: DataEntryMetricModel): void {
    const typeMap: Record<string, number> = {
      [Constants.METRICS_TYPE.CUSTOM]: Constants.METRICS_TYPE_ID.CUSTOM,
      [Constants.METRICS_TYPE.GLOBAL]: Constants.METRICS_TYPE_ID.GLOBAL_LOCAL,
      [Constants.METRICS_TYPE.LOCAL]: Constants.METRICS_TYPE_ID.GLOBAL_LOCAL,
      [Constants.METRICS_TYPE.DIMENSION]: Constants.METRICS_TYPE_ID.DIMENSION,
    };

    const itemTypeId = [typeMap[metric.type!]];

    this._genericFloatButtons([
      {
        key: "systemLog",
        data: {
          itemId: metric.id,
          itemTypeId: itemTypeId,
        },
      },
    ]);
  }
}
